import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import Header from '../components/layout/Header';
import AnnouncementBar from '../components/layout/AnnouncementBar';
import Footer from '../components/layout/Footer';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';

// Social media links for floating icons
const socialMediaLinks = [
  {
    name: "TikTok",
    url: "https://www.tiktok.com/@itsyungdexy04",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
    color: "#000000",
  },
  {
    name: "Instagram",
    url: "https://www.instagram.com/itsyungdexy/",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    color: "#E4405F",
  },
  {
    name: "YouTube",
    url: "https://www.youtube.com/@itsyungdexy",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    color: "#FF0000",
  },
];

// Floating Social Icon Component
const FloatingSocialIcon = ({ social, index }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const startX = Math.random() * 100;
    const startY = 20 + Math.random() * 60;
    setPosition({ x: startX, y: startY });
    
    const animate = () => {
      setPosition(prev => ({
        x: prev.x + (Math.sin(Date.now() / 2000 + index) * 0.3),
        y: prev.y + (Math.cos(Date.now() / 3000 + index) * 0.2),
      }));
    };
    
    const interval = setInterval(animate, 50);
    return () => clearInterval(interval);
  }, [index]);

  return (
    <a
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed z-40 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:scale-125 transition-all duration-300 shadow-lg"
      style={{
        left: `${Math.min(Math.max(position.x, 5), 90)}%`,
        top: `${Math.min(Math.max(position.y, 15), 85)}%`,
        color: social.color,
      }}
      title={social.name}
    >
      {social.icon}
    </a>
  );
};

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, addToCart, loading } = useCart();
  const { formatPrice } = useCurrency();
  const [selectedSize, setSelectedSize] = useState('M');
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [added, setAdded] = useState(false);

  // Find product by ID
  const product = products.find((p) => p.id === id) || products[0];
  
  // Find related products (same type, different id)
  const relatedProducts = products.filter(
    p => p.type === product?.type && p.id !== product?.id
  ).slice(0, 4);

  const images = product?.images || [];
  const sizes = product?.sizes || ['S', 'M', 'L', 'XL', '2XL'];

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleAddToCart = async () => {
    if (!product) return;
    setAddingToCart(true);
    const success = await addToCart(product.id, selectedSize, quantity);
    setAddingToCart(false);
    if (success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;
    setAddingToCart(true);
    const success = await addToCart(product.id, selectedSize, quantity);
    setAddingToCart(false);
    if (success) {
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white/60">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Product not found</p>
          <Link to="/shop" className="text-white underline">Back to Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a] relative">
      <AnnouncementBar />
      <Header />
      
      {/* Floating Social Media Icons */}
      {socialMediaLinks.map((social, index) => (
        <FloatingSocialIcon key={social.name} social={social} index={index} />
      ))}
      
      <main className="py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Breadcrumb */}
          <nav className="text-sm mb-8">
            <Link to="/" className="text-white/50 hover:text-white/70">Home</Link>
            <span className="mx-2 text-white/30">/</span>
            <Link to="/shop" className="text-white/50 hover:text-white/70">Shop</Link>
            <span className="mx-2 text-white/30">/</span>
            <span className="text-white">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Images */}
            <div>
              <div className="relative aspect-square overflow-hidden bg-white/10 rounded-lg mb-4">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Image Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>
              
              {/* Thumbnail Navigation */}
              {images.length > 1 && (
                <div className="flex gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 border-2 overflow-hidden rounded transition-colors ${
                        selectedImage === index ? 'border-white' : 'border-transparent hover:border-white/50'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <p className="text-xs text-white/50 mb-2 tracking-wider">YUNG WORLD</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                {product.name}
              </h1>
              <p className="text-lg text-white/60 mb-4">{product.color}</p>
              <p className="text-3xl font-bold text-white mb-6">{formatPrice(product.price)}</p>
              
              {product.description && (
                <p className="text-white/70 mb-8 leading-relaxed whitespace-pre-line">{product.description}</p>
              )}

              {/* Size Selection */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-white">Size</label>
                  <button className="text-xs text-white/50 underline hover:text-white">Size Guide</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      data-testid={`size-${size}`}
                      className={`w-14 h-12 border text-sm font-medium transition-all rounded ${
                        selectedSize === size
                          ? 'bg-white text-black border-white'
                          : 'border-white/30 text-white hover:border-white'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-8">
                <label className="text-sm font-medium text-white block mb-3">Quantity</label>
                <div className="flex items-center border border-white/30 w-fit rounded">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                    data-testid="quantity-decrease"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-12 text-center font-medium text-white" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                    data-testid="quantity-increase"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <button 
                onClick={handleAddToCart}
                disabled={addingToCart}
                data-testid="add-to-cart-btn"
                className={`w-full py-4 text-sm font-medium tracking-wider transition-colors mb-4 flex items-center justify-center gap-2 rounded ${
                  added 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white text-black hover:bg-gray-200'
                }`}
              >
                {addingToCart ? (
                  <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></span>
                ) : added ? (
                  <>
                    <Check size={20} />
                    ADDED TO CART
                  </>
                ) : (
                  `ADD TO CART - ${formatPrice(product.price * quantity)}`
                )}
              </button>

              {/* Buy Now */}
              <button 
                onClick={handleBuyNow}
                disabled={addingToCart}
                data-testid="buy-now-btn"
                className="w-full border-2 border-white text-white py-4 text-sm font-medium tracking-wider hover:bg-white hover:text-black transition-colors rounded"
              >
                BUY IT NOW
              </button>

              {/* Additional Info */}
              <div className="mt-8 pt-8 border-t border-white/20 space-y-3">
                <p className="text-sm text-white/60 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  Free shipping on orders over {formatPrice(50)}
                </p>
                <p className="text-sm text-white/60 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  {product.type === 'hoodie' ? 'Premium heavyweight fleece' : 'Premium heavyweight cotton'}
                </p>
                <p className="text-sm text-white/60 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  Screen printed graphics
                </p>
                <p className="text-sm text-white/60 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  True to size fit
                </p>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">YOU MAY ALSO LIKE</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {relatedProducts.map((relProduct) => (
                  <Link
                    key={relProduct.id}
                    to={`/product/${relProduct.id}`}
                    className="group"
                  >
                    <div className="aspect-square overflow-hidden bg-white/10 rounded-lg mb-4">
                      <img
                        src={relProduct.images?.[0]}
                        alt={relProduct.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <p className="text-xs text-white/50 mb-1">Yung World</p>
                    <h3 className="text-sm font-medium text-white group-hover:text-white/80">
                      {relProduct.name} - {relProduct.color}
                    </h3>
                    <p className="mt-1 text-sm font-bold text-white">
                      {formatPrice(relProduct.price)}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductPage;
